/**
 * Client-side FCM Web Push module.
 *
 * requestPushPermission() — prompt user, get token, register with server
 * onForegroundMessage(cb)  — listen for messages while app is focused
 */

import { getMessaging, getToken, onMessage, isSupported, Messaging } from 'firebase/messaging';
import { getFirebaseApp, getFirebaseConfig, getFirebaseAuth } from './firebase';

let messagingInstance: Messaging | null = null;

async function getMessagingInstance(): Promise<Messaging | null> {
  if (messagingInstance) return messagingInstance;

  const supported = await isSupported();
  if (!supported) {
    console.warn('[push] Firebase Messaging is not supported in this browser');
    return null;
  }

  const app = await getFirebaseApp();
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

/**
 * Register the service worker and pass the Firebase config to it.
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[push] Service workers not supported');
    return null;
  }

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

  // Wait for the SW to be active before posting config
  const sw = registration.installing || registration.waiting || registration.active;
  if (sw) {
    const config = await getFirebaseConfig();
    const sendConfig = () => {
      sw.postMessage({ type: 'FIREBASE_CONFIG', config });
    };

    if (sw.state === 'activated') {
      sendConfig();
    } else {
      sw.addEventListener('statechange', () => {
        if (sw.state === 'activated') sendConfig();
      });
    }
  }

  return registration;
}

/**
 * Register the FCM token with the server.
 */
async function registerTokenWithServer(token: string): Promise<void> {
  const auth = await getFirebaseAuth();
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not authenticated');

  const res = await fetch('/api/user/fcm-token', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ token }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to register token');
  }
}

/**
 * Request notification permission, get the FCM token, and register
 * it with the server. Returns the token on success, null on denial/error.
 */
export async function requestPushPermission(): Promise<string | null> {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('[push] Permission denied');
      return null;
    }

    const messaging = await getMessagingInstance();
    if (!messaging) return null;

    const swRegistration = await registerServiceWorker();
    if (!swRegistration) return null;

    const config = await getFirebaseConfig();
    const vapidKey = config.vapidKey;
    if (!vapidKey) {
      console.error('[push] No VAPID key in Firebase config');
      return null;
    }

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    if (!token) {
      console.warn('[push] No FCM token returned');
      return null;
    }

    await registerTokenWithServer(token);
    console.log('[push] Token registered successfully');
    return token;
  } catch (error) {
    console.error('[push] Error requesting permission:', error);
    return null;
  }
}

/**
 * Listen for foreground messages (app is focused).
 * Returns an unsubscribe function.
 */
export function onForegroundMessage(
  callback: (payload: { title?: string; body?: string; data?: Record<string, string> }) => void,
): () => void {
  let unsubscribe: (() => void) | null = null;

  getMessagingInstance().then((messaging) => {
    if (!messaging) return;
    unsubscribe = onMessage(messaging, (payload) => {
      callback({
        title: payload.notification?.title,
        body: payload.notification?.body,
        data: payload.data,
      });
    });
  });

  return () => {
    if (unsubscribe) unsubscribe();
  };
}
